import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useStudio } from "./StudioContext";
import { StudioPage, Panel, Drawer, Loading, Empty, Badge, SectionLabel, DBlock, DList, DKV, resolveDept, isSdk, cx } from "./StudioShared";
import { Users, Cpu } from "lucide-react";

const titleCase = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Strategists() {
  const { strategists } = useStudio();
  const [params, setParams] = useSearchParams();
  const [dept, setDept] = useState("");
  const [domain, setDomain] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const focus = params.get("focus");
  useEffect(() => { if (focus) setOpenId(focus); }, [focus]);

  const domains = useMemo(() => [...new Set((strategists || []).flatMap((s) => s.domains || []))].sort(), [strategists]);
  const depts = useMemo(() => [...new Set((strategists || []).map((s) => resolveDept(s).name))], [strategists]);

  if (!strategists) return <StudioPage title="Strategist Explorer" question="Who provides expertise?"><Loading /></StudioPage>;

  const filtered = strategists.filter((s) => {
    if (dept && resolveDept(s).name !== dept) return false;
    if (domain && !(s.domains || []).includes(domain)) return false;
    if (type === "sdk" && !isSdk(s)) return false;
    if (type === "legacy" && isSdk(s)) return false;
    if (q && !((s.name + s.id + (s.domains || []).join(" ") + (s.mission || "")).toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const current = strategists.find((s) => s.id === openId);
  const close = () => { setOpenId(null); if (params.get("focus")) { params.delete("focus"); setParams(params, { replace: true }); } };

  const sel = "bg-navy-800 border border-white/10 text-white text-sm px-3 py-2 rounded-sm focus:border-gold focus:outline-none";

  return (
    <StudioPage title="Strategist Explorer" question="Who provides expertise?" testid="strategists-explorer">
      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Filters */}
        <Panel className="p-4 h-fit space-y-3" data-testid="strategist-filters">
          <div><label className="text-xs text-slate-500 block mb-1">Department</label>
            <select value={dept} onChange={(e) => setDept(e.target.value)} className={cx(sel, "w-full")} data-testid="filter-department"><option value="">All</option>{depts.map((d) => <option key={d} value={d}>{d}</option>)}</select></div>
          <div><label className="text-xs text-slate-500 block mb-1">Domain</label>
            <select value={domain} onChange={(e) => setDomain(e.target.value)} className={cx(sel, "w-full")} data-testid="filter-domain"><option value="">All</option>{domains.map((d) => <option key={d} value={d}>{d}</option>)}</select></div>
          <div><label className="text-xs text-slate-500 block mb-1">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className={cx(sel, "w-full")} data-testid="filter-type"><option value="">All</option><option value="sdk">SDK</option><option value="legacy">Legacy</option></select></div>
          <div><label className="text-xs text-slate-500 block mb-1">Search</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="name, domain…" className={cx(sel, "w-full")} data-testid="filter-search" /></div>
          <button onClick={() => { setDept(""); setDomain(""); setType(""); setQ(""); }} className="text-xs text-gold hover:underline" data-testid="filter-reset">Reset filters</button>
          <div className="text-xs text-slate-500 pt-2 border-t border-white/5">{filtered.length} of {strategists.length}</div>
        </Panel>

        {/* Grid */}
        {filtered.length === 0 ? <Empty text="No strategists match these filters." /> : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3 content-start" data-testid="strategists-grid">
            {filtered.map((s) => {
              const d = resolveDept(s);
              return (
                <button key={s.id} onClick={() => setOpenId(s.id)} data-testid={`strategist-card-${s.id}`}
                  className="text-left bg-navy-800 border border-white/10 hover:border-gold/40 rounded-sm p-4 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <span className="w-9 h-9 border border-gold/40 rounded-sm flex items-center justify-center text-gold shrink-0"><Cpu size={16} /></span>
                    {isSdk(s) ? <Badge tone="gold">SDK{s.code ? ` · ${s.code}` : ""}</Badge> : <Badge tone="slate">Legacy</Badge>}
                  </div>
                  <div className="text-white font-medium text-sm">{s.name}</div>
                  <div className="text-slate-500 text-xs mt-0.5">{d.name}{d.source === "generated" && <span className="text-slate-600"> · auto</span>}</div>
                  <div className="flex flex-wrap gap-1 mt-2.5">
                    {(s.domains || []).slice(0, 4).map((dm) => <span key={dm} className="text-[10px] text-slate-400 bg-white/5 px-1.5 py-0.5 rounded-sm">{dm}</span>)}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <Drawer open={!!current} onClose={close} testid="strategist-drawer" title={current?.name} subtitle={current ? `${current.code || current.id} · v${current.version || "1.0"}` : ""}>
        {current && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              {isSdk(current) ? <Badge tone="gold">SDK v{current.sdk_version}</Badge> : <Badge tone="slate">Legacy</Badge>}
              {(() => { const d = resolveDept(current); return <Badge tone={d.source === "sdk" ? "green" : "slate"}>{d.name}{d.source === "generated" ? " · auto-classified" : ""}</Badge>; })()}
              {current.industry && <Badge tone="slate">{titleCase(current.industry)}</Badge>}
            </div>
            {current.mission && <DBlock title="Mission"><p className="text-slate-300 text-sm leading-relaxed">{current.mission}</p></DBlock>}
            {current.scope && <DBlock title="Scope"><p className="text-slate-300 text-sm leading-relaxed">{current.scope}</p></DBlock>}
            <DList title="Domains" items={current.domains} />
            <DList title="Tags" items={current.tags} />
            {(current.recommended_sequence?.length > 0 || current.related_strategists?.length > 0) && (
              <div className="space-y-1.5 border-t border-white/5 pt-3" data-testid="strategist-collaboration">
                <SectionLabel className="text-gold mb-1">Collaboration</SectionLabel>
                {current.recommended_sequence?.length > 0 && <DKV label="Recommended sequence" value={current.recommended_sequence.join(" → ")} />}
                {current.related_strategists?.length > 0 && <DKV label="Works with" value={current.related_strategists.join(", ")} />}
              </div>
            )}
            {current.boundaries && <DBlock title="Boundaries"><p className="text-slate-400 text-sm leading-relaxed">{current.boundaries}</p></DBlock>}
          </>
        )}
      </Drawer>
    </StudioPage>
  );
}
